import { useRef, useState } from 'react';

import { keys, values } from '@client/new/helpers/general';
import { F1, TMap } from '@client/new/helpers/types';
import { VideoThumbnail } from './VideoThumbnail';

type VideoTimeLineGeneratorProps = {
  duration: number;
  src: string;
  framesCount: number;
  onRender: F1<Thumbnail[]>;
  loader?: JSX.Element;
  height: number;
  thumbnailWidth: number;
  startTime?: number;
  onClick?: (timestamp: number) => void;
};

type Thumbnail = { src: string; timestamp: number };

/** interpolate between two values with given scale */
const lerp = (a: number, b: number, t: number) => a + t * (b - a);

const VideoTimeLineGenerator = ({
  framesCount,
  ...p
}: VideoTimeLineGeneratorProps) => {
  const images = useRef<TMap<number, Thumbnail>>({});
  const onRender = (i: number) => (data: Thumbnail) => {
    images.current[i] = data;
    if (keys(images.current).length === framesCount)
      p.onRender(values(images.current));
  };

  const getSnapshotTime = (i: number) =>
    lerp(0, p.duration, (1 / framesCount) * i);

  return (
    <>
      {p.loader || <p>generating frames...</p>}
      {Array.from({ length: framesCount }).map((_, i) => (
        <div key={i} style={{ display: 'none' }}>
          <VideoThumbnail
            videoUrl={p.src}
            snapshotAtTime={getSnapshotTime(i)}
            cors={false}
            renderThumbnail={false}
            thumbnailHandler={onRender(i)}
            height={p.height}
            width={p.thumbnailWidth}
          />
        </div>
      ))}
    </>
  );
};

type VideoTimeLineRenderer = VideoTimeLineProps & {
  b64Images: Array<{ src: string; timestamp: number }>;
  onClick?: (timestamp: number) => void;
};

const VideoTimeLineRenderer = (p: VideoTimeLineRenderer) => (
  <div
    style={{
      width: `${p.width}px`,
      height: `${p.height}px`,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'row',
    }}
  >
    {p.b64Images.map(({ src, timestamp }, i) => (
      <div
        key={i}
        style={{ flexShrink: 0 }}
        onClick={() => p.onClick && p.onClick(timestamp)}
      >
        <img src={src} height={p.height} />
      </div>
    ))}
  </div>
);

type VideoTimeLineProps = {
  width: number;
  src: string;
  ratio: number;
  onClick?: (timestamp: number) => void;
} & Pick<VideoTimeLineGeneratorProps, 'loader' | 'height' | 'duration'>;

export const VideoTimeLine = (p: VideoTimeLineProps) => {
  const [b64Images, setB64Images] = useState<
    Array<{ src: string; timestamp: number }>
  >([]);
  const thumbnailWidth = Math.round(p.ratio * p.height);

  return b64Images.length ? (
    <VideoTimeLineRenderer {...p} b64Images={b64Images} />
  ) : (
    <VideoTimeLineGenerator
      duration={p.duration}
      framesCount={Math.ceil(p.width / thumbnailWidth)}
      src={p.src}
      onRender={setB64Images}
      height={p.height}
      thumbnailWidth={thumbnailWidth}
    />
  );
};
